import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, FieldErrors } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { WidgetPreview } from '@/components/widget-builder/WidgetPreview'
import { toast } from '@/components/ui/use-toast'
import {
	widgetFormSchema,
	type WidgetFormValues,
	defaultWidgetValues,
} from '@/lib/widget-schema'
import { Breadcrumb } from '@/components/Breadcrumb'
import { Edit, Loader2, ArrowLeft, ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'
import Stepper from '@/components/widget-builder/Stepper' // Import the Stepper
import { Step1_Functionality } from '@/components/widget-builder/steps/Step1_Functionality'
import { Step2_Content } from '@/components/widget-builder/steps/Step2_Content'
import { Step3_Appearance } from '@/components/widget-builder/steps/Step3_Appearance'
import { Step4_Behavior } from '@/components/widget-builder/steps/Step4_Behavior'
import { useProgram } from '@/hooks/useProgram'
import { useTeamSubscription } from '@/hooks/useTeamSubscription' // Import subscription hook
import { apiClient } from '@/lib/api'
import { useTeamContext } from '@/context/TeamContext'
import PreviewArrow from '@/components/landing/PreviewArrow'
import { useTranslation } from '@/hooks/useTranslation'

// Use the centralized default values from schema to ensure consistency
const defaultFormValues: WidgetFormValues = defaultWidgetValues

const CreateWidget = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [currentStep, setCurrentStep] = useState(1)
	const totalSteps = 4
	const stepNames = [
		t('createWidget.stepFunctionality'),
		t('createWidget.stepContent'),
		t('createWidget.stepAppearance'),
		t('createWidget.stepBehavior')
	]
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [limitError, setLimitError] = useState<string | null>(null)
	const { programId, loading: programLoading } = useProgram()
	const { teamId, isLoading: isTeamLoading } = useTeamContext() // Get teamId using the hook
	const { data: subscriptionData, isLoading: isSubscriptionLoading } =
		useTeamSubscription(teamId) // Get subscription status

	// Determine premium status (handle loading state)
	const isPremium = useMemo(() => {
		if (isSubscriptionLoading) return false // Default to false while loading
		return subscriptionData?.isPremium || false
	}, [subscriptionData, isSubscriptionLoading])

	// Initialize form with default values
	const form = useForm<WidgetFormValues>({
		resolver: zodResolver(widgetFormSchema),
		defaultValues: defaultFormValues, // Use the defined defaults
		mode: 'onChange', // Update preview live
	})

	// --- Watch the entire form state ---
	const watchedConfig = form.watch()

	// Helper to convert array to object (if using the temporary array)
	const convertArrayToObject = (
		arr: { key: string; value: string }[] | undefined
	) => {
		const obj: Record<string, string> = {}
		if (Array.isArray(arr)) {
			arr.forEach((item) => {
				if (item.key && item.value) {
					obj[item.key] = item.value
				}
			})
		}
		return obj
	}

	// --- Handle Invalid Submission ---
	const handleInvalidSubmit = (errors: FieldErrors<WidgetFormValues>) => {
		if (errors.name) {
			toast({
				title: t('createWidget.missingInformation'),
				description: t('createWidget.provideWidgetName'),
				variant: 'destructive',
			})
		} else {
			console.error('Form validation errors:', errors)
			toast({
				title: t('createWidget.validationError'),
				description: t('createWidget.checkFormErrors'),
				variant: 'destructive',
			})
		}
	}

	const handleSubmit = async (values: WidgetFormValues) => {
		setIsSubmitting(true)
		setLimitError(null) // Reset error on new submission attempt

		if (!programId) {
			toast({
				title: t('createWidget.error'),
				description: t('createWidget.selectProgramBeforeCreating'),
				variant: 'destructive',
			})
			setIsSubmitting(false)
			return
		}

		// Ensure program_id is included and sync userProperties if using array
		const submissionValues = {
			...values,
			program_id: programId,
			targeting: {
				...values.targeting,
				// Use helper function to convert array to object, using 'as any' for the temporary field
				userProperties: convertArrayToObject(
					(values.targeting as any).userPropertiesArray
				),
			},
		}
		delete (submissionValues as any).widgetTriggerSize
		delete (submissionValues as any).widgetTriggerIcon
		// Remove the temporary array if it exists
		delete (submissionValues.targeting as any).userPropertiesArray

		try {
			// Use the apiClient to make the request
			const newWidget = await apiClient<{ id: string }>('/api/widgets', {
				method: 'POST',
				data: submissionValues,
				needsAuth: true, // Assuming this endpoint requires authentication
			})

			if (!newWidget?.id) {
				throw new Error(t('createWidget.widgetCreationNoId'))
			}

			navigate(`/widgets/success/${newWidget.id}`)
		} catch (error: any) {
			console.error('Error creating widget via API:', error)
			let description =
				error.message || t('createWidget.failedToCreateWidget')

			// Check for specific error codes from the backend
			if (
				error.code === 'free_tier_limit' ||
				error.code === 'activation_limit_reached'
			) {
				description = error.message
				setLimitError(description)
				toast({
					title: t('createWidget.freeTierLimitReached'),
					description,
					variant: 'default',
				})
			} else {
				toast({
					title: t('createWidget.error'),
					description,
					variant: 'destructive',
				})
			}
		} finally {
			setIsSubmitting(false)
		}
	}

	const nextStep = () => {
		// Validate step 1 (name field) before proceeding
		if (currentStep === 1) {
			const nameValue = form.getValues('name')
			if (!nameValue || nameValue.trim() === '') {
				form.setError('name', {
					type: 'required',
					message: t('createWidget.widgetNameRequired')
				})
				// Focus on the name field to help user
				const nameField = document.querySelector('input[name="name"]') as HTMLInputElement
				if (nameField) {
					nameField.focus()
				}
				return // Don't advance to next step
			}
			// Clear any existing error if validation passes
			form.clearErrors('name')
		}
		
		setCurrentStep((prev) => Math.min(prev + 1, totalSteps))
	}
	const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1))

	// Loading state for initial data dependencies
	const initialLoading =
		isTeamLoading || programLoading || isSubscriptionLoading

	if (initialLoading) {
		// You might want a more specific skeleton here later
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		)
	}

	return (
		<div className="p-4 md:p-8 max-w-7xl mx-auto">
			<Breadcrumb
				className="mb-6"
				items={[
					{ name: t('createWidget.dashboard'), url: '/' },
					{ name: t('createWidget.widgets'), url: '/widgets' },
					{ name: t('createWidget.createWidget'), url: '/widgets/create' },
				]}
			/>
			<div className="space-y-6">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
					<div>
						<div className="flex items-center gap-2">
							<div className="p-2 bg-primary/10 rounded-lg">
								<Edit className="h-6 w-6 text-primary" />
							</div>
							<h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
								{t('createWidget.createNewWidget')}
							</h1>
						</div>
						<p className="text-sm md:text-base text-muted-foreground mt-1">
							{t('createWidget.followStepsDescription')}
						</p>
					</div>
					{/* Moved Save/Cancel buttons to wizard footer */}
				</motion.div>

				{/* Stepper */}
				<Stepper
					currentStep={currentStep}
					totalSteps={totalSteps}
					stepNames={stepNames}
					onStepClick={(step) => setCurrentStep(step)}
				/>

				{/* Wizard Content */}
				<Form {...form}>
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						{/* Left Column: Steps */}
						<motion.div
							key={currentStep} // Add key to trigger animation on step change
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: 20 }}
							transition={{ duration: 0.3 }}
							className="lg:col-span-2 space-y-6">
							{/* Render current step */}
							{currentStep === 1 && (
								<Step1_Functionality form={form} isPremium={isPremium} />
							)}
							{currentStep === 2 && (
								<Step2_Content form={form} isPremium={isPremium} />
							)}
							{currentStep === 3 && (
								<Step3_Appearance form={form} isPremium={isPremium} />
							)}
							{currentStep === 4 && (
								<Step4_Behavior form={form} isPremium={isPremium} />
							)}

							{/* Navigation Buttons */}
							<div className="flex justify-between pt-6 border-t">
								<Button
									variant="outline"
									onClick={prevStep}
									disabled={currentStep === 1}
									type="button">
									{' '}
									{/* Ensure type is button to prevent form submission */}
									<ArrowLeft className="mr-2 h-4 w-4" /> {t('createWidget.previous')}
								</Button>
								{currentStep < totalSteps ? (
									<Button onClick={nextStep} type="button">
										{t('createWidget.next')} <ArrowRight className="ml-2 h-4 w-4" />
									</Button>
								) : (
									<Button
										onClick={form.handleSubmit(
											handleSubmit,
											handleInvalidSubmit
										)}
										disabled={isSubmitting || !programId || !!limitError}
										type="button">
										{isSubmitting ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												{t('createWidget.creating')}
											</>
										) : limitError ? (
											t('createWidget.upgradeRequired')
										) : (
											t('createWidget.createWidget')
										)}
									</Button>
								)}
							</div>
						</motion.div>

						{/* Right Column: Preview */}
						<div className="lg:sticky lg:top-8 lg:h-[calc(100vh-4rem)]">
							{/* Use 'as any' for config prop if WidgetPreview expects program_id which isn't in watchedConfig */}
							<WidgetPreview config={watchedConfig as any} showManualTrigger={true} />
						</div>
					</div>
				</Form>
			</div>
			
			{/* Preview Arrow */}
			<PreviewArrow />
		</div>
	)
}

export default CreateWidget
